// Where the circle's own furniture goes, once the camera has said where the
// ground is: the distance rings, the spoke to the open mark, and the nudge that
// keeps a drawn box inside the block (SPEC-redesign §5.2, §5.6, §6.3).
//
// Each takes the elements it writes as parameters, so none of it needs the
// closure that mounts the wheel — and the per-frame hot path `reproject` runs
// is readable on its own.
import { distanceLabel } from '../shared/geo.ts';
import type { ViewState } from '../state/viewstate.ts';
import { ringsFor } from './wheel-geometry.ts';

export function paintRings(ringLayer: HTMLElement, disc: number, view: ViewState, dial: boolean) {
  ringLayer.replaceChildren();
  // Ring labels disappear in dial mode, and so do the rings (§6.3).
  if (dial) return;
  const perMetre = disc / 2 / view.radius;
  for (const ring of ringsFor(view.radius, disc)) {
    const el = document.createElement('div');
    el.className = 'circle__ring';
    el.setAttribute('aria-hidden', 'true');
    const size = ring * perMetre * 2;
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    const caption = document.createElement('span');
    caption.className = 'mono';
    caption.textContent = distanceLabel(ring);
    el.append(caption);
    ringLayer.append(el);
  }
}

/**
 * The 2 px line from the centre to the selected mark (§5.6).
 *
 * The viewBox is the OVERLAY's box, not `--disc` squared: since Decision 46
 * the overlay is a rectangle, and a square viewBox on a rectangular SVG is
 * scaled and centred by `xMidYMid meet` — every spoke silently wrong, with
 * nothing on screen to say so.
 */
export function drawSpoke(
  overlay: HTMLElement,
  spoke: SVGElement,
  spokeLine: SVGElement,
  at: { x: number; y: number } | null,
) {
  const frame = overlay.getBoundingClientRect();
  spoke.setAttribute('viewBox', `0 0 ${frame.width} ${frame.height}`);
  spoke.style.display = at ? '' : 'none';
  if (!at) return;
  spokeLine.setAttribute('x1', String(frame.width / 2));
  spokeLine.setAttribute('y1', String(frame.height / 2));
  spokeLine.setAttribute('x2', String(at.x));
  spokeLine.setAttribute('y2', String(at.y));
}

/**
 * Keeps every drawn box inside the block without moving the mark (#177, F2).
 *
 * The anchor is the ground and the box is a picture of it: the anchor keeps
 * the position `map.project` gave it — which is what „the disc spans exactly
 * twice the radius" measures — and only the box is nudged, by the one or two
 * pixels a 22 px badge overhangs the 10 px of clearance above the ring.
 *
 * The block, not the overlay: the strip and the attribution are opaque paper
 * ABOVE every mark (§5.2), so a mark that reaches under them is hidden by
 * them, and clamping to the overlay would pull marks off the map's edge for
 * no gain. The three loops are one read phase between two write phases; one
 * loop would measure a box while the previous element's write was pending.
 */
const DRAWN = '.circle__badge, .circle__tag';

/**
 * Measures every drawn box once, after a rebuild, and remembers its size.
 *
 * This is the layout read that used to happen on every frame, and taking it off
 * that path is half of §4.3's budget work. A box's SIZE is a fact about its
 * label — „174 ×2" is 55,9 px whatever the camera does — so it survives every
 * pan and every pinch, and only a rebuild can change it. The nudge is cleared
 * first because a nudged box is measured where it was pushed, not where it
 * stands.
 */
export function measureMark(el: HTMLElement) {
  // One box, for the live path. `measureMarks` clears every nudge and reads
  // every box, which is right after a rebuild and ruinous on a gesture frame:
  // the ring passes over something on most frames of a pan at a wide radius, so
  // a whole-layer read was happening seven times a second at 6× CPU. A mark
  // that has just been created carries no nudge to clear, so this is the read
  // alone, for the one element that needs it.
  const drawn = el.getBoundingClientRect();
  el.dataset.w = String(drawn.width);
  el.dataset.h = String(drawn.height);
}

export function measureMarks(markLayer: HTMLElement) {
  const boxes = [...markLayer.querySelectorAll<HTMLElement>(DRAWN)];
  for (const el of boxes) {
    el.style.removeProperty('--nudge-x');
    el.style.removeProperty('--nudge-y');
  }
  // One read phase after the write phase, exactly as the three-loop version
  // was: reading inside the loop above would measure a box while the previous
  // element's style write was still pending.
  for (const el of boxes) {
    const drawn = el.getBoundingClientRect();
    el.dataset.w = String(drawn.width);
    el.dataset.h = String(drawn.height);
  }
}

export function clampMarks(markLayer: HTMLElement, section: HTMLElement, overlay: HTMLElement) {
  const block = section.getBoundingClientRect();
  const frame = overlay.getBoundingClientRect();
  for (const el of markLayer.querySelectorAll<HTMLElement>(DRAWN)) {
    const anchor = el.parentElement;
    const w = Number(el.dataset.w);
    const h = Number(el.dataset.h);
    // A box nobody has measured yet is one this frame cannot place. It is left
    // where it is rather than guessed at; the rebuild that drew it measures it.
    if (!anchor || !Number.isFinite(w) || !Number.isFinite(h) || !w) continue;
    // Where the box is, from numbers alone: the anchor's own projection, which
    // `reproject` has just written, less half the box it is centred on.
    const left = frame.left + Number(anchor.dataset.x) - w / 2;
    const top = frame.top + Number(anchor.dataset.y) - h / 2;
    const x = Math.max(0, block.left - left) - Math.max(0, left + w - block.right);
    const y = Math.max(0, block.top - top) - Math.max(0, top + h - block.bottom);
    // Written only when there is one, so the nearly-all marks that are nowhere
    // near an edge keep an empty style attribute, as they always have.
    setNudge(el, '--nudge-x', x);
    setNudge(el, '--nudge-y', y);
  }
}

function setNudge(el: HTMLElement, name: string, value: number) {
  if (value === 0) el.style.removeProperty(name);
  else el.style.setProperty(name, `${value}px`);
}
